import { useContext, useEffect, useState } from "react";
import { AuthContext } from "~/utils/useAuth";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";

interface FeatureFeedbackFormProps {
  formId: string;
  title?: string;
  description?: string;
  height?: string;
  style?: React.CSSProperties;
  hideWhenEditing?: boolean;
  isEditing?: boolean;
  feature?: string;
  spread?: string; // Reused for any context ID (spread ID, card name, etc.)
}

export const FeatureFeedbackForm = ({
  formId,
  title = "Feature Feedback",
  description,
  height = "150",
  style,
  hideWhenEditing = false,
  isEditing = false,
  feature,
  spread
}: FeatureFeedbackFormProps) => {
  const [isTallyLoaded, setIsTallyLoaded] = useState(false);
  const { user } = useContext(AuthContext);
  const { appDarkMode } = useSnapshot(localState);

  useEffect(() => {
    // Add Tally script if it doesn't exist
    const script = document.createElement("script");
    script.innerHTML = `var d=document,w="https://tally.so/widgets/embed.js",v=function(){"undefined"!=typeof Tally?Tally.loadEmbeds():d.querySelectorAll("iframe[data-tally-src]:not([src])").forEach((function(e){e.src=e.dataset.tallySrc}))};if("undefined"!=typeof Tally)v();else if(d.querySelector('script[src="'+w+'"]')==null){var s=d.createElement("script");s.src=w,s.onload=v,s.onerror=v,d.body.appendChild(s);}`;
    document.body.appendChild(script);

    // Load embeds when user is available and not in editing mode
    if (typeof (window as any).Tally !== 'undefined' && user && !isEditing) {
      setIsTallyLoaded(false);
      (window as any).Tally.loadEmbeds();
      setTimeout(() => setIsTallyLoaded(true), 500);
    }
  }, [user, isEditing]);

  if (hideWhenEditing && isEditing) {
    return null;
  }

  return (
    <div style={{
      fontSize: '12px',
      opacity: 0.9,
      padding: '26px',
      borderRadius: '12px',
      backgroundColor: appDarkMode ? '#e8e8e8' : '#fff',
      color: '#000',
      filter: appDarkMode ? 'invert(1) hue-rotate(180deg)' : undefined,
      lineHeight: '1.6',
      width: '100%',
      textAlign: 'center',
      marginTop: '40px',
      marginBottom: '20px',
      position: 'relative',
      ...style
    }}>
      {description && (
        <div style={{ marginBottom: '-30px' }}>
          {description}
        </div>
      )}

      {/* Loading indicator */}
      {!isTallyLoaded && (
        <div style={{
          position: 'absolute',
          top: '80px',
          left: 0,
          right: 0,
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.5
        }}>
          <SimpleLoadingIndicator
            style={{
              width: '24px',
              height: '24px',
              position: 'static'
            }}
          />
        </div>
      )}

      <iframe
        data-tally-src={`https://tally.so/embed/${formId}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1${user?.email ? `&email=${user.email}` : ''}${user?.username ? `&username=${user.username}` : ''}${user?.name ? `&name=${user.name}` : ''}${feature ? `&feature=${feature}` : ''}${spread ? `&spread=${spread}` : ''}`}
        loading="lazy"
        width="100%"
        height={height}
        style={{
          border: 'none',
          background: 'transparent',
          margin: 0,
          padding: 0,
          opacity: isTallyLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
        title={title}
      />
    </div>
  );
};
